// Admin — posts management (PRD §3.9): all posts, status filter, newest first,
// with edit / publish / unpublish / delete. This is also the /admin landing.

import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../../auth/AuthContext";
import { api, ApiError } from "../../lib/api";
import type { AdminPost } from "../../lib/types";
import { formatDateTime, formatMonthYear, timeAgo } from "../../lib/time";
import { AppHeader } from "../../components/AppHeader";
import { AdminNav } from "../../components/AdminNav";
import { InlineVideo } from "../../components/InlineVideo";
import { Button } from "../../components/Button";
import { Field } from "../../components/Field";

type Filter = "all" | "published" | "draft";
const FILTERS: { key: Filter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "published", label: "Published" },
  { key: "draft", label: "Drafts" },
];

// Date-range filter, to help Messy find and jump back to old posts over time.
type DateFilter =
  | { kind: "all" }
  | { kind: "this-month" }
  | { kind: "last-month" }
  | { kind: "month"; ym: string }; // "YYYY-MM"

/** "YYYY-MM" for the 1st of the month `offset` months from now. */
function ym(offset: number): string {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offset);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function monthParam(f: DateFilter): string | undefined {
  if (f.kind === "all") return undefined;
  if (f.kind === "this-month") return ym(0);
  if (f.kind === "last-month") return ym(-1);
  return f.ym;
}

function dateFilterLabel(f: DateFilter): string {
  if (f.kind === "all") return "All dates";
  if (f.kind === "this-month") return "This month";
  if (f.kind === "last-month") return "Last month";
  return formatMonthYear(f.ym);
}

export function AdminPosts() {
  const { user, setPreviewPublic } = useAuth();
  useEffect(() => setPreviewPublic(false), [setPreviewPublic]);

  // Admin first-login welcome / tour (PRD §3.8) — fires once on Messy's first login.
  const [showWelcome, setShowWelcome] = useState(user?.isFirstLogin ?? false);

  const [filter, setFilter] = useState<Filter>("all");
  const [dateFilter, setDateFilter] = useState<DateFilter>({ kind: "all" });
  const [dateModalOpen, setDateModalOpen] = useState(false);
  const [republishTarget, setRepublishTarget] = useState<AdminPost | null>(null);
  const [posts, setPosts] = useState<AdminPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async (f: Filter, df: DateFilter) => {
    setLoading(true);
    setError(null);
    try {
      const { posts } = await api.adminPosts(f, monthParam(df));
      setPosts(posts);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't load your posts.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(filter, dateFilter);
  }, [load, filter, dateFilter]);

  async function act(id: number, fn: () => Promise<unknown>) {
    setBusyId(id);
    try {
      await fn();
      await load(filter, dateFilter);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "That didn't work — try again.");
    } finally {
      setBusyId(null);
    }
  }

  function onDelete(post: AdminPost) {
    if (confirm(`Delete "${post.title}"? This can't be undone.`)) {
      void act(post.id, () => api.deletePost(post.id));
    }
  }

  // Republishing a post that already has a publishedAt means it was published before
  // (then unpublished) — ask whether to keep that original date or bump it to today.
  function onPublishClick(post: AdminPost) {
    if (post.publishedAt) {
      setRepublishTarget(post);
    } else {
      void act(post.id, () => api.publishPost(post.id, "now"));
    }
  }

  return (
    <div className="min-h-dvh bg-paper pb-20">
      <AppHeader />
      <main className="mx-auto w-full max-w-xl px-5 py-6">
        <AdminNav />

        {showWelcome ? (
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="ink-block mb-6 rounded-2xl bg-citrus-wash/60 p-5"
          >
            <h2 className="font-display text-3xl text-ink">Welkom, Messy! 🍊</h2>
            <p className="mt-2 text-sm leading-relaxed text-ink-soft">
              This is your control room. Tap <strong>New Post</strong> to share what you're up to —
              add text, photos and video, save a draft, then publish when you're ready. Newest posts
              show up top. Use <strong>Go To Public</strong> up in the header any time to see exactly
              what your friends see.
            </p>
            <button
              onClick={() => setShowWelcome(false)}
              className="stamp mt-4 text-xs text-ink-muted underline decoration-dotted underline-offset-2 hover:text-ink"
            >
              Got it
            </button>
          </motion.section>
        ) : null}

        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-ink">Posts</h1>
          <div className="flex items-center gap-2">
            <Link
              to="/admin/posts/new"
              className="inline-flex items-center gap-1.5 rounded-md border-2 border-ink bg-paper px-4 py-2 text-sm font-semibold text-citrus-deep shadow-[2px_2px_0_0_var(--color-ink)] transition-transform active:translate-x-[1px] active:translate-y-[1px] active:shadow-none"
            >
              New Post 🍊
            </Link>
          </div>
        </div>

        {/* Status filter + date-range jump, same row */}
        <div className="mt-5 flex flex-wrap items-center gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={
                "rounded-full border-2 px-3 py-1 text-sm font-semibold transition-colors " +
                (filter === f.key
                  ? "border-ink bg-ink text-paper"
                  : "border-ink/20 text-ink-soft hover:border-ink/50")
              }
            >
              {f.label}
            </button>
          ))}
          <button
            onClick={() => setDateModalOpen(true)}
            className="ml-auto inline-flex items-center gap-1 rounded-full border-2 border-ink/20 px-3 py-1 text-sm font-semibold text-ink-soft transition-colors hover:border-ink/50"
          >
            {dateFilterLabel(dateFilter)}
            <span aria-hidden className="text-xs">▾</span>
          </button>
        </div>

        {error ? (
          <p className="mt-6 rounded-lg border-2 border-love/30 bg-love/5 px-3 py-2 text-sm text-love">
            {error}
          </p>
        ) : null}

        {loading ? (
          <div className="flex justify-center py-20">
            <span className="h-8 w-8 animate-spin rounded-full border-[3px] border-citrus/30 border-t-citrus" />
          </div>
        ) : posts.length === 0 ? (
          <div className="mt-10 rounded-2xl border-2 border-dashed border-ink/20 px-6 py-14 text-center">
            <p className="font-display text-2xl text-ink">Nothing here yet</p>
            <p className="mt-1 text-sm text-ink-muted">
              {filter === "all" ? "Dala what you must — write your first dispatch." : "No posts in this view."}
            </p>
          </div>
        ) : (
          <ul className="mt-6 flex flex-col gap-4">
            {posts.map((post) => (
              <li key={post.id} className="overflow-hidden rounded-xl border-2 border-ink/15 bg-white">
                {post.videoUrl || post.videoStreamUid ? (
                  <InlineVideo videoUrl={post.videoUrl} videoStreamUid={post.videoStreamUid} posterUrl={post.coverImageUrl} />
                ) : post.coverImageUrl ? (
                  <div className="w-full overflow-hidden bg-paper-deep">
                    <img
                      src={post.coverImageUrl}
                      alt=""
                      className="block w-full"
                      loading="lazy"
                    />
                  </div>
                ) : null}

                <div className="p-4">
                  <div className="flex items-center gap-2">
                    <StatusBadge status={post.status} />
                    <span className="text-xs text-ink-muted">
                      {post.status === "published"
                        ? timeAgo(post.publishedAt)
                        : `edited ${timeAgo(post.updatedAt)}`}
                    </span>
                  </div>
                  <h3 className="mt-1.5 truncate text-lg font-bold text-ink">{post.title}</h3>
                  {post.snippet ? (
                    <p className="mt-0.5 line-clamp-1 text-sm text-ink-muted">{post.snippet}</p>
                  ) : null}

                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <Link
                      to={`/admin/posts/${post.id}/edit`}
                      className="rounded-lg border-2 border-ink/25 px-3 py-1.5 text-sm font-semibold text-ink-soft hover:border-ink"
                    >
                      Edit
                    </Link>
                    {post.status === "draft" ? (
                      <button
                        disabled={busyId === post.id}
                        onClick={() => onPublishClick(post)}
                        className="rounded-lg border-2 border-ink bg-citrus px-3 py-1.5 text-sm font-semibold text-paper disabled:opacity-60"
                      >
                        Publish
                      </button>
                    ) : (
                      <button
                        disabled={busyId === post.id}
                        onClick={() => void act(post.id, () => api.unpublishPost(post.id))}
                        className="rounded-lg border-2 border-ink/25 px-3 py-1.5 text-sm font-semibold text-ink-soft hover:border-ink disabled:opacity-60"
                      >
                        Unpublish
                      </button>
                    )}
                    {post.status === "published" ? (
                      <Link
                        to={`/post/${post.id}`}
                        className="rounded-lg border-2 border-ink/25 px-3 py-1.5 text-sm font-semibold text-ink-soft hover:border-ink"
                      >
                        View
                      </Link>
                    ) : null}
                    <button
                      disabled={busyId === post.id}
                      onClick={() => onDelete(post)}
                      className="ml-auto rounded-lg px-3 py-1.5 text-sm font-semibold text-love/80 hover:text-love disabled:opacity-60"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </main>

      {dateModalOpen ? (
        <DateFilterModal
          current={dateFilter}
          onClose={() => setDateModalOpen(false)}
          onApply={(f) => {
            setDateFilter(f);
            setDateModalOpen(false);
          }}
        />
      ) : null}

      {republishTarget ? (
        <RepublishModal
          post={republishTarget}
          onCancel={() => setRepublishTarget(null)}
          onConfirm={(mode) => {
            const id = republishTarget.id;
            setRepublishTarget(null);
            void act(id, () => api.publishPost(id, mode));
          }}
        />
      ) : null}
    </div>
  );
}

function DateFilterModal({
  current,
  onClose,
  onApply,
}: {
  current: DateFilter;
  onClose: () => void;
  onApply: (f: DateFilter) => void;
}) {
  const [step, setStep] = useState<"choose" | "month">(current.kind === "month" ? "month" : "choose");
  const [pickedYm, setPickedYm] = useState(current.kind === "month" ? current.ym : ym(0));
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const OPTIONS: { key: "all" | "this-month" | "last-month"; label: string }[] = [
    { key: "all", label: "All" },
    { key: "this-month", label: "This month" },
    { key: "last-month", label: "Last month" },
  ];

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-6 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === overlayRef.current) onClose();
      }}
    >
      <div className="w-full max-w-sm overflow-hidden rounded-2xl border-2 border-ink/10 bg-paper shadow-[0_8px_32px_rgba(0,0,0,0.18)]">
        <div className="flex items-center justify-between border-b-2 border-ink/10 px-5 py-4">
          <h2 className="text-lg font-bold text-ink">Jump to a date</h2>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-ink-muted hover:bg-paper-deep hover:text-ink"
          >
            ×
          </button>
        </div>

        <div className="px-5 py-5">
          {step === "choose" ? (
            <div className="flex flex-col gap-2">
              {OPTIONS.map((o) => (
                <button
                  key={o.key}
                  onClick={() => onApply({ kind: o.key })}
                  className={
                    "rounded-xl border-2 px-4 py-3 text-left font-semibold transition-colors " +
                    (current.kind === o.key
                      ? "border-ink bg-ink text-paper"
                      : "border-ink/15 text-ink hover:border-ink/40 hover:bg-paper-deep")
                  }
                >
                  {o.label}
                </button>
              ))}
              <button
                onClick={() => setStep("month")}
                className={
                  "rounded-xl border-2 px-4 py-3 text-left font-semibold transition-colors " +
                  (current.kind === "month"
                    ? "border-ink bg-ink text-paper"
                    : "border-ink/15 text-ink hover:border-ink/40 hover:bg-paper-deep")
                }
              >
                Select month…
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <Field label="Month" type="month" value={pickedYm} onChange={(e) => setPickedYm(e.target.value)} />
              <div className="flex gap-3">
                <Button variant="ghost" type="button" onClick={() => setStep("choose")} className="flex-1">
                  Back
                </Button>
                <Button
                  type="button"
                  onClick={() => onApply({ kind: "month", ym: pickedYm })}
                  disabled={!pickedYm}
                  className="flex-1"
                >
                  Jump
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RepublishModal({
  post,
  onCancel,
  onConfirm,
}: {
  post: AdminPost;
  onCancel: () => void;
  onConfirm: (mode: "now" | "original") => void;
}) {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCancel();
    }
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onCancel]);

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-6 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === overlayRef.current) onCancel();
      }}
    >
      <div className="w-full max-w-sm overflow-hidden rounded-2xl border-2 border-ink/10 bg-paper shadow-[0_8px_32px_rgba(0,0,0,0.18)]">
        <div className="px-5 py-5">
          <h2 className="text-lg font-bold text-ink">Publish "{post.title}" again?</h2>
          <p className="mt-2 text-sm text-ink-muted">
            This one was already published before, on{" "}
            <span className="font-semibold text-ink-soft">{formatDateTime(post.publishedAt)}</span>. Put it
            back at that original date, or publish it as brand new, today?
          </p>

          <div className="mt-5 flex flex-col gap-2">
            <Button type="button" onClick={() => onConfirm("original")}>
              Keep original date
            </Button>
            <Button variant="ghost" type="button" onClick={() => onConfirm("now")}>
              Publish as new (today)
            </Button>
            <button
              type="button"
              onClick={onCancel}
              className="stamp mt-1 text-xs text-ink-muted underline decoration-dotted underline-offset-2 hover:text-ink"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: AdminPost["status"] }) {
  const published = status === "published";
  return (
    <span
      className={
        "stamp inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[0.6rem] " +
        (published ? "bg-citrus/15 text-citrus-deep" : "bg-ink/10 text-ink-muted")
      }
    >
      <span className={"h-1.5 w-1.5 rounded-full " + (published ? "bg-citrus" : "bg-ink-muted")} />
      {published ? "Published" : "Draft"}
    </span>
  );
}
